import React, { useEffect, useRef, useState } from 'react';
import { auth } from '../../firebaseConfig';
import { backendBase, apiPost } from '../../utils/backendApi';
import { ACTIVE, Shot } from './shared';

// Live browser pane ([[studio-journey]]): the screencast of a browse run.
// GET /lab/:org/runs/:id/cast is SSE behind a bearer token, so it is read
// with fetch + ReadableStream rather than EventSource. Frames are JPEG
// base64 swapped into one <img>; the step caption comes from the same
// stream (and from labRun.live, which Firestore streams anyway). Without
// Redis on the plane the stream says `nocast` and the pane shows the last
// keyframe the runner put in S3 instead — never a blank box.
//
// The first frame arrives ~2 minutes after start (Fargate boot ≈ 1 min,
// then the planner + server-side searches ≈ 1 min with the browser still
// on about:blank). Two minutes of a pulsing dot over a static box read as
// "broken" (2026-10-13), so the wait box narrates the run's stage instead.

// labRun.stage / step captions → what the wait box says while there is no
// frame yet. The runner writes the stage; this only rewords it.
function warmupText(run, live) {
  if (live?.caption) return live.caption;
  if (run.status === 'queued') return 'Starting a browser machine — about a minute…';
  const s = String(run.stage || '');
  if (/^searching:/.test(s)) return `Searching for “${s.replace(/^searching:\s*/, '')}” — the first page appears when the searches are done…`;
  if (s && s !== 'queued') return `${s.charAt(0).toUpperCase()}${s.slice(1)}…`;
  return 'Opening a browser…';
}

export default function LiveBrowser({ org, run, canEdit, busy, act }) {
  const [frame, setFrame] = useState('');
  const [note, setNote] = useState(null);
  const [state, setState] = useState('connecting'); // connecting | waiting | live | keyframes | ended
  const abortRef = useRef(null);
  const active = ACTIVE.has(run.status);

  // The same pane instance is reused when the selected run changes (a new run
  // replaces a finished one in the same slot): drop the old run's frame and
  // state so a fresh run does not inherit "ended" or a stale picture.
  useEffect(() => { setFrame(''); setNote(null); setState('connecting'); }, [run.id]);

  useEffect(() => {
    if (!active) { setState('ended'); return undefined; }
    let stopped = false; let attempt = 0;
    const connect = async () => {
      const user = auth.currentUser;
      if (!user || stopped) return;
      const ctrl = new AbortController(); abortRef.current = ctrl;
      try {
        const token = await user.getIdToken();
        const res = await fetch(`${backendBase()}/lab/${org.id}/runs/${run.id}/cast`, { headers: { Authorization: `Bearer ${token}`, Accept: 'text/event-stream' }, signal: ctrl.signal });
        if (!res.ok || !res.body) throw new Error(`cast ${res.status}`);
        attempt = 0;
        const reader = res.body.getReader(); const dec = new TextDecoder(); let buf = '';
        for (;;) {
          const { value, done } = await reader.read();
          if (done || stopped) break;
          buf += dec.decode(value, { stream: true });
          let i;
          while ((i = buf.indexOf('\n\n')) >= 0) {
            const chunk = buf.slice(0, i); buf = buf.slice(i + 2);
            const line = chunk.split('\n').find((l) => l.startsWith('data:'));
            if (!line) continue;
            let ev; try { ev = JSON.parse(line.slice(5)); } catch { continue; }
            if (ev.t === 'frame') { setFrame(`data:image/jpeg;base64,${ev.data}`); setState('live'); }
            else if (ev.t === 'step') setNote(ev);
            else if (ev.t === 'hello') { setState((s) => (s === 'connecting' ? 'waiting' : s)); if (ev.live) setNote(ev.live); }
            else if (ev.t === 'nocast') setState((s) => (s === 'live' ? s : 'keyframes'));
            else if (ev.t === 'end') { setState('ended'); stopped = true; }
          }
        }
      } catch (err) {
        if (stopped || ctrl.signal.aborted) return;
      }
      if (!stopped) { attempt += 1; setTimeout(connect, Math.min(1000 * 2 ** attempt, 10000)); }
    };
    connect();
    return () => { stopped = true; abortRef.current?.abort(); };
  }, [org.id, run.id, active]);

  const live = note || run.live || {};
  const keyframe = run.live?.keyframeKey || null;
  return (
    <div className="lab-live" aria-live="polite">
      <div className="lab-live-bar">
        {active ? <span className="lab-live-rec" aria-hidden="true" /> : null}
        <strong>{active ? (state === 'live' ? 'Live' : state === 'connecting' ? 'Connecting…' : state === 'waiting' ? (run.status === 'queued' ? 'Starting' : 'Getting ready') : 'Browsing') : 'Finished'}</strong>
        <span className="lab-live-url" title={live.url}>{live.url || ''}</span>
        {live.of ? <span className="mf-muted">{live.step || 0}/{live.of}</span> : null}
        {active && canEdit && <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" disabled={busy === `cancel-${run.id}`} onClick={() => act(`cancel-${run.id}`, () => apiPost(`/lab/${org.id}/runs/${run.id}/cancel`, {}))}>Stop</button>}
      </div>
      <div className="lab-live-frame">
        {frame ? <img src={frame} alt={live.title ? `Browser showing ${live.title}` : 'Live browser'} />
          : keyframe ? <Shot org={org} runId={run.id} k={keyframe} large auto alt={live.title || 'Browser keyframe'} />
            : <div className="lab-live-wait">{active ? warmupText(run, live) : 'No frames were captured.'}</div>}
      </div>
      {live.caption && (frame || keyframe) && <p className="lab-live-cap">{live.caption}</p>}
    </div>
  );
}
